import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Grid,
  Alert, // Added Alert
} from "@mui/material";
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import {
  TpHeiDropdown,
  ProgramDeliveryDropdown,
} from "../../CourseCatalog/DropDowns";
import InstructorDropdown from "./InstructorDropdown";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const Add = ({ onRecordAdded }) => {
  const { addData: addFirestoreData } = useFirestore("DeliveryDetails"); // Use Firestore hook
  const { data: heiPrograms } = useFirestore("HeiPrograms"); // Fetch HEI programs for CTE code lookup
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const initialFormData = {
    ProgramDeliveryID: "",
    HEIProgramCode: "",
    InstructorID: "",
    CTE_Program_Cd: "",
    MVTPCstrand: "",
    Notes: "",
    Career_Field_Cd: "",
    Active: 0,
    StrandNonactive: 0,
    InstructorNonactiveInStrand: 0,
    SRGradClass: 0,
    JRclass: 0,
  };
  const [formData, setFormData] = useState(initialFormData);

  // Function to get CTE Program Code from HEI Program Code
  const getCteProgCodeFromHei = (heiProgramCode) => {
    if (!heiPrograms || !heiProgramCode) return "";

    const heiProgram = heiPrograms.find(
      (program) =>
        program.ProgramCode === heiProgramCode ||
        program.HEI_Program_Code === heiProgramCode ||
        program.HEIProgramCode === heiProgramCode,
    );

    return heiProgram?.CTE_Program_Code || heiProgram?.CTE_Program_Cd || "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const updatedData = {
      ...formData,
      [name]: value,
    };

    // If HEI Program Code changed, set CTE Program Code to the same value
    if (name === "HEIProgramCode") {
      updatedData.CTE_Program_Cd = value;
    }

    setFormData(updatedData);
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked ? -1 : 0,
    }));
  };

  const handleSubmit = async () => {
    // Validate required dropdown fields
    if (
      !formData.ProgramDeliveryID ||
      formData.ProgramDeliveryID.trim() === ""
    ) {
      setSnackbarMessage("Program Delivery ID is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!formData.HEIProgramCode || formData.HEIProgramCode.trim() === "") {
      setSnackbarMessage("HEI Program Code is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!formData.InstructorID || formData.InstructorID.trim() === "") {
      setSnackbarMessage("Instructor ID is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    setSnackbarMessage("");
    try {
      await addFirestoreData(formData); // addData from useFirestore handles createdAt
      setSnackbarMessage("Record added successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setFormData(initialFormData);
      setOpen(false);
      onRecordAdded(); // Call the callback function to refresh the list
    } catch (error) {
      console.error("Error adding record:", error);
      setSnackbarMessage(`Error adding record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      <Button
        variant="contained"
        color="success"
        onClick={() => setOpen(true)}
        sx={{ marginBottom: "10px", color: "white" }}
      >
        Add Record
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleClose}>Add Record</ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <ProgramDeliveryDropdown
                value={formData.ProgramDeliveryID}
                onChange={handleChange}
                name="ProgramDeliveryID"
                label="Program Delivery ID"
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TpHeiDropdown
                value={formData.HEIProgramCode}
                onChange={handleChange}
                name="HEIProgramCode"
                label="HEI Program Code"
                fullWidth
                size="medium"
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <InstructorDropdown
                value={formData.InstructorID}
                onChange={handleChange}
                name="InstructorID"
                label="Instructor ID"
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="CTE Program Code"
                name="CTE_Program_Cd"
                value={formData.CTE_Program_Cd}
                fullWidth
                margin="normal"
                disabled
                helperText="Auto-populated based on HEI Program Code selection"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="MVTPC Strand"
                name="MVTPCstrand"
                value={formData.MVTPCstrand}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Notes"
                name="Notes"
                value={formData.Notes}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Career Field Code"
                name="Career_Field_Cd"
                value={formData.Career_Field_Cd}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Active === -1}
                    onChange={handleCheckboxChange}
                    name="Active"
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.StrandNonactive === -1}
                    onChange={handleCheckboxChange}
                    name="StrandNonactive"
                  />
                }
                label="Strand Nonactive"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.InstructorNonactiveInStrand === -1}
                    onChange={handleCheckboxChange}
                    name="InstructorNonactiveInStrand"
                  />
                }
                label="Instructor Nonactive In Strand"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.SRGradClass === -1}
                    onChange={handleCheckboxChange}
                    name="SRGradClass"
                  />
                }
                label="SR Grad Class"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.JRclass === -1}
                    onChange={handleCheckboxChange}
                    name="JRclass"
                  />
                }
                label="JR Class"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions onSave={handleSubmit} saving={loading} />
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Add;
